import {
  Boxes,
  FolderKanban,
  Info,
  Layers,
  Package,
  Smartphone,
} from "lucide-react";
import { useSingleEquipment } from "@/hooks/useEquipment";
import { useSimDevices } from "@/hooks/useSims";
import { matchContextPath } from "../match";
import type { ContextParams, NavContextDef, NavItem } from "../types";

export const equipmentPath = (id: string) => `/equipment/${id}`;

function useEquipmentItems({ id }: ContextParams): NavItem[] {
  const { data: equipment } = useSingleEquipment(id);
  const { data: simDevices } = useSimDevices();
  const canHoldSims =
    !!equipment?.is_serialized &&
    simDevices.some((device) => device.equipmentID === id);
  const base = equipmentPath(id);
  const items: NavItem[] = [
    {
      key: "information",
      label: "Information",
      href: base,
      exact: true,
      icon: Info,
    },
    {
      key: "contents",
      label: "Contents",
      href: `${base}/contents`,
      icon: Boxes,
    },
    {
      key: "projects",
      label: "Projects",
      href: `${base}/projects`,
      icon: FolderKanban,
    },
  ];
  if (equipment?.is_serialized) {
    items.push({
      key: "instances",
      label: "Instances",
      href: `${base}/instances`,
      icon: Layers,
    });
  }
  if (canHoldSims) {
    items.push({
      key: "sims",
      label: "SIM cards",
      href: `${base}/sims`,
      icon: Smartphone,
    });
  }
  return items;
}

function useEquipmentHeader({ id }: ContextParams) {
  const { data: equipment } = useSingleEquipment(id);
  if (!equipment) return {};
  const kicker = [equipment.number, equipment.manufacturer]
    .filter(Boolean)
    .join(" • ");
  return { kicker: kicker || null, title: equipment.name };
}

export const equipmentContext: NavContextDef = {
  type: "equipment",
  typeLabel: "Equipment",
  icon: Package,
  match: (pathname) =>
    matchContextPath("/equipment/:id", pathname, {
      id: ["tags", "tag", "untagged", "new"],
    }),
  key: ({ id }) => `equipment:${id}`,
  base: ({ id }) => equipmentPath(id),
  parent: () => ({ label: "Equipment", href: "/equipment" }),
  useHeader: useEquipmentHeader,
  useItems: useEquipmentItems,
};
